/**
 * BroadcastModal
 * Modale per inviare un annuncio push a un gruppo di utenti.
 * Usata da admin/superadmin desktop. Salva messaggio in DB,
 * invia push notification a tutti i destinatari + broadcast WS live.
 */

import { useState } from 'react';
import { X, Megaphone, Users, Send, AlertCircle, CheckCircle2 } from 'lucide-react';

type Group = 'all' | 'mobile-operators' | 'mobile-officina' | 'desktop';

interface BroadcastModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GROUP_OPTIONS: { value: Group; label: string; description: string }[] = [
  { value: 'all', label: 'Tutti', description: 'Operatori mobile + utenti desktop' },
  { value: 'mobile-operators', label: 'Operatori accettazione', description: 'Mobile operator (accettazione/admin)' },
  { value: 'mobile-officina', label: 'Operatori officina', description: 'Mobile officina' },
  { value: 'desktop', label: 'Solo desktop', description: 'Utenti desktop' }
];

export default function BroadcastModal({ isOpen, onClose }: BroadcastModalProps) {
  const [group, setGroup] = useState<Group>('all');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; recipients?: number; saved?: number; pushed?: number; error?: string } | null>(null);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const userId = (() => {
        try {
          const token = localStorage.getItem('accessToken');
          if (!token) return '';
          const payload = JSON.parse(atob(token.split('.')[1]));
          return payload.id || payload.sub || '';
        } catch { return ''; }
      })();
      const userName = localStorage.getItem('userName') || 'Admin';

      const res = await fetch('/api/dm/broadcast', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-name': userName,
          'x-user-type': 'desktop'
        },
        body: JSON.stringify({ group, message, title: title || undefined })
      });
      const body = await res.json();
      setResult(body);
      if (body.success) {
        setMessage('');
        setTitle('');
        setTimeout(() => { setResult(null); onClose(); }, 3000);
      }
    } catch (err) {
      setResult({ success: false, error: (err as Error).message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-xl border border-zinc-700 w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-purple-500/20 to-blue-600/20 border-b border-purple-500/30 p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Megaphone className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-white">Invia annuncio</h3>
              <p className="text-sm text-purple-200/80 mt-0.5">Notifica push a un gruppo di utenti</p>
            </div>
            <button
              onClick={onClose}
              disabled={sending}
              className="p-2 hover:bg-zinc-700 rounded-lg transition-colors text-gray-400 hover:text-white disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Selettore gruppo */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Destinatari
            </label>
            <div className="grid grid-cols-2 gap-2">
              {GROUP_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setGroup(opt.value)}
                  disabled={sending}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    group === opt.value
                      ? 'bg-purple-500/20 border-purple-500/50 text-white'
                      : 'bg-zinc-800/50 border-zinc-700 text-gray-300 hover:border-zinc-600'
                  }`}
                >
                  <div className="font-semibold text-sm">{opt.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{opt.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Titolo (opzionale) */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Titolo (opzionale)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={80}
              placeholder="Es. Pausa pranzo"
              disabled={sending}
              className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Messaggio */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Messaggio <span className="text-red-400">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder="Scrivi il tuo annuncio…"
              disabled={sending}
              className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
            />
            <div className="text-xs text-gray-500 text-right mt-1">{message.length}/500</div>
          </div>

          {/* Risultato */}
          {result && (
            <div className={`rounded-lg p-3 border ${
              result.success ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'
            }`}>
              {result.success ? (
                <div className="flex items-start gap-2 text-sm text-green-300">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    Annuncio inviato a <strong>{result.recipients}</strong> destinatari.
                    Push consegnate: <strong>{result.pushed}</strong>.
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 text-sm text-red-300">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>Errore: {result.error || 'Invio fallito'}</div>
                </div>
              )}
            </div>
          )}

          {/* Azioni */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={onClose}
              disabled={sending}
              className="flex-1 px-4 py-2.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg border border-zinc-600 transition-colors disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-500 active:bg-purple-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Invio…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Invia annuncio
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
